import { useState, useEffect, CSSProperties } from "react";
import ModalSymphony from "../../elements/ModalSymphony";
import ButtonSymphony from "../../elements/ButtonSymphony";
import { clsx, formatNumberToDisplayType, styx } from "modules/Utils";
import Colors from "modules/Colors";
import Icon from "elements/Icon";
import Loader from "elements/Loader";
import useBrand, { BrandExistsState } from "Hooks/useBrand";
import { connectToBrand_api } from "api/brandApi";
import useScreen from "Hooks/useScreen";
import { toastError } from "modules/Toast";
import useFacebookConnections from "Hooks/useFacebookConnections";
import { AdConnectionType } from "modules/Const";
import useTikTokConnections from "Hooks/useTikTokConnections";
import { AdAccountPageType, CurrentBrand, FacebookAdAccount, FacebookPageType, FBPixel, PixelConversionEvent, TikTokAdAccount, FBCustomAudience } from "modules/Types";
import InputSymphony from "../../elements/InputSymphony";
import millify from "millify";
import { FB_AD_ACCOUNT_STATUS } from "helpers/FB";
import Sty from "modules/Sty";
import dayjs from "dayjs";
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import { queryFB } from "helpers/FB";
import useAppView from "Hooks/useAppView";

const ICON_SIZE = 50;
const THROTTLED_MODE = true;
const PAGINATION_LIMIT = 50;

/** Zero state configurations for different connection types */
const ZERO_STATE_CONFIGS: Partial<Record<AdConnectionType, {
    icon: string;
    title: string;
    subtitle: string;
    primaryButton: {
        text: string;
        variant: 'primary' | 'outlined' | 'text';
        iconRight?: string;
        onClick: () => void;
    };
    secondaryButton?: {
        text: string;
        variant: 'primary' | 'outlined' | 'text';
        onClick: () => void;
    };
}>> = {
    [AdConnectionType.FACEBOOK_PAGE]: {
        icon: 'facebook',
        title: 'No Facebook Pages Found',
        subtitle: 'You need at least one Facebook Page to run ads. Create a page for your brand or business to get started.',
        primaryButton: {
            text: 'Create a Facebook Page',
            variant: 'primary',
            iconRight: 'click',
            onClick: () => window.open('https://www.facebook.com/pages/create', '_blank')
        },
        secondaryButton: {
            text: 'Learn more about Facebook Pages',
            variant: 'text',
            onClick: () => window.open('https://www.facebook.com/help/104002523024878', '_blank')
        }
    },
    [AdConnectionType.FACEBOOK_AD_ACCOUNT]: {
        icon: 'facebook',
        title: 'No Facebook Ad Accounts Found',
        subtitle: 'You need at least one Facebook Ad Account to run ads. Create an ad account to get started.',
        primaryButton: {
            text: 'Create a Facebook Ad Account',
            variant: 'primary',
            iconRight: 'click',
            onClick: () => window.open('https://business.facebook.com/overview', '_blank')
        },
        secondaryButton: {
            text: 'Learn more about Ad Accounts',
            variant: 'text',
            onClick: () => window.open('https://www.facebook.com/business/help/407323696966570', '_blank')
        }
    },
    [AdConnectionType.FACEBOOK_PIXEL]: {
        icon: 'facebook',
        title: 'No Facebook Pixels Found',
        subtitle: 'You need at least one Facebook Pixel to track conversions. Create a pixel to get started.',
        primaryButton: {
            text: 'Create a Facebook Pixel',
            variant: 'primary',
            iconRight: 'click',
            onClick: () => window.open('https://business.facebook.com/events_manager', '_blank')
        },
        secondaryButton: {
            text: 'Learn more about Pixels',
            variant: 'text',
            onClick: () => window.open('https://www.facebook.com/business/help/952192354843755', '_blank')
        }
    },
    [AdConnectionType.FACEBOOK_CONVERSION_EVENT]: {
        icon: 'facebook',
        title: 'No Conversion Events Found',
        subtitle: 'No conversion events were found for your selected pixel. You can still proceed without selecting a specific event.',
        primaryButton: {
            text: 'Learn about Conversion Events',
            variant: 'primary',
            iconRight: 'click',
            onClick: () => window.open('https://www.facebook.com/business/help/952192354843755', '_blank')
        }
    },
    [AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE]: {
        icon: 'facebook',
        title: 'No Custom Audiences Found',
        subtitle: 'No custom audiences found in your Facebook Ad Account. Create custom audiences in Facebook Ads Manager to get started.',
        primaryButton: {
            text: 'Create Custom Audiences',
            variant: 'primary',
            iconRight: 'click',
            onClick: () => window.open('https://business.facebook.com/adsmanager/audiences', '_blank')
        },
        secondaryButton: {
            text: 'Learn about Custom Audiences',
            variant: 'text',
            onClick: () => window.open('https://www.facebook.com/business/help/744354708981227', '_blank')
        }
    },
    [AdConnectionType.TIKTOK_AD_ACCOUNT]: {
        icon: 'tiktok',
        title: 'No TikTok Ad Accounts Found',
        subtitle: 'You need at least one TikTok Ad Account to run ads. Create an ad account to get started.',
        primaryButton: {
            text: 'Create a TikTok Ad Account',
            variant: 'primary',
            iconRight: 'click',
            onClick: () => window.open('https://business.tiktok.com/', '_blank')
        },
        secondaryButton: {
            text: 'Learn more about TikTok Ads',
            variant: 'text',
            onClick: () => window.open('https://business.tiktok.com/help', '_blank')
        }
    }
};

/** Props for the social connect modal component */
interface AdConnectionModalProps {
    /** Additional class name */
    className?: string;
    /** Additional inline styles */
    style?: CSSProperties;
    /** Whether the modal is open */
    isOpen: boolean;
    /** Callback when modal is closed */
    onClose: () => void;
    onConnect?: (connectedProfile: any) => void;
    connectionType?: AdConnectionType;
    /** Whether to use direct connect flow */
    isDirectConnect?: boolean;
    /** Whether to validate business account */
    validateBusinessAccount?: boolean;
    isConnectToBrand?: boolean;
    /** For custom audiences - callback when multiple audiences are selected */
    onImportMultiple?: (audiences: FBCustomAudience[]) => void;
    /** Whether to allow multiple selection (for custom audiences) */
    allowMultipleSelection?: boolean;
    /** Subtitle for the modal */
    subtitle?: string;
    /** Whether to show copy from existing brand option */
    showCopyFromExistingBrand?: boolean;
    /** List of brands available for copying connections */
    availableBrandsForCopy?: CurrentBrand[];
    /** Callback when copying from an existing brand */
    onCopyFromBrand?: (sourceBrand: CurrentBrand) => void;
    /** Whether this connection is being shown as a prerequisite for another connection */
    isPrerequisite?: boolean;
    /** The original connection type that was requested (when showing prerequisite) */
    originalConnectionType?: AdConnectionType;
    /** Brand ID to use for API calls (overrides currentBrand) */
    brandId?: number;
}

interface ButtonConfig {
    /** Button text */
    text: string;
    /** Button variant */
    variant?: 'primary' | 'outlined' | 'text';
    /** Button size */
    size?: 'small' | 'medium' | 'large';
    /** Click handler */
    onClick: () => void;
    /** Icon to show on the right */
    iconRight?: string;
}

/** Interface for formatted social connect data */
interface FormattedAdConnectionData {
    /** Unique identifier for the connection */
    id: number | string;
    /** Display title */
    title: string;
    /** Whether to hide the ID */
    hideId?: boolean;
    /** Account status */
    status?: string;
    /** Profile/thumbnail image URL */
    thumbnail?: string;
    /** Whether the account is funded */
    funded?: boolean;
    /** Parent name - for example the Business Center */
    parentName?: string;
    /** Parent ID - for example the Business */
    parentId?: string;
    /** Whether the account has an issue */
    issueDetails?: {
        title?: string;
        actionText?: string;
        actionUrl?: string;
    }
    /** Additional info */
    additionalInfo?: string | JSX.Element;
}

/** Type for handling social connect data */
type AdConnectionData = TikTokAdAccount | AdAccountPageType | FacebookPageType | FBPixel | PixelConversionEvent | FBCustomAudience

const AdConnectionModal = ({
    className,
    style,
    isOpen,
    subtitle,
    onClose,
    onConnect,
    connectionType = AdConnectionType.TIKTOK_AD_ACCOUNT,
    onImportMultiple,
    allowMultipleSelection = false,
    showCopyFromExistingBrand = false,
    availableBrandsForCopy = [],
    onCopyFromBrand,
    isPrerequisite = false,
    originalConnectionType,
    brandId,
}: AdConnectionModalProps) => {
    const { currentBrand } = useBrand() as BrandExistsState;
    const { mobileView } = useScreen();
    const {
        isTeamView
    } = useAppView()

    const {
        conversionsEventName,
        setConversionsEventName,
    } = useCampaignBuilder();

    const {
        selectedFacebookAdAccount,
        selectedFacebookPage,
        selectedFacebookPixel,
        getFacebookAdAccounts,
        setFacebookAdAccount,
        getFacebookPages,
        isLoadingFacebookPages,
        isLoadingFacebookAdAccounts,
        setFacebookPage,
        loggedInFacebookUser,
        renderFacebookLoginButton,
        isLoadingFacebookPixels,
        setFacebookPixel,
        getFacebookPixels,
        getConversionEvents,
        isLoadingFacebookConversionEvents,
        getFacebookCustomAudiences,
    } = useFacebookConnections();

    const {
        tiktokAdAccount,
        getTikTokAdAccounts,
        setTiktokAdAccount
    } = useTikTokConnections()

    useEffect(() => {
        if (isOpen) {
            // Reset copy view when modal opens
            setShowCopyFromBrandView(false);
            handleGetData()
        }
    }, [connectionType, isOpen]);

    /** Get connection details for a brand based on connection type */
    const getBrandConnectionDetails = (brand: CurrentBrand) => {
        switch (connectionType) {
            case AdConnectionType.FACEBOOK_AD_ACCOUNT:
                return {
                    connected: Boolean(brand.connections?.facebook_ad_account),
                    name: brand.connections?.facebook_ad_account?.name,
                    details: brand.connections?.facebook_ad_account ? 
                        `$${millify(brand.connections.facebook_ad_account.amount_spent || 0)} spent` : null
                };
            case AdConnectionType.FACEBOOK_PAGE:
                return {
                    connected: Boolean(brand.connections?.facebook_page),
                    name: brand.connections?.facebook_page?.name,
                    details: brand.connections?.facebook_page?.followers_count ? 
                        `${millify(brand.connections.facebook_page.followers_count)} followers` : null
                };
            case AdConnectionType.FACEBOOK_PIXEL:
                return {
                    connected: Boolean(brand.connections?.facebook_pixel),
                    name: brand.connections?.facebook_pixel?.name ?? brand.connections?.facebook_pixel?.id,
                    details: 'Pixel for conversion tracking'
                };
            case AdConnectionType.FACEBOOK_CONVERSIONS_API_TOKEN:
                return {
                    connected: Boolean(brand.connections?.facebook_pixel_conversions_api_access_token),
                    name: 'Conversions API Token',
                    details: 'Server-side conversion tracking'
                };
            case AdConnectionType.FACEBOOK_PROFILE:
                return {
                    connected: Boolean(brand.connections?.logged_in_fb_user),
                    name: brand.connections?.logged_in_fb_user?.name,
                    details: 'Facebook account for managing ads'
                };
            default:
                return { connected: false, name: null, details: null };
        }
    };


    const connectionOptions: Partial<Record<AdConnectionType, {
        title: string;
        subtitle: string;
        footer?: string | JSX.Element;
        serviceName: string;
        errorComponent?: JSX.Element | string;
    }>> = {
        [AdConnectionType.TIKTOK_AD_ACCOUNT]: {
            title: 'TikTok Ad Account',
            subtitle: `Select the TikTok Ad Account you'd like to use to run ads.`,
            serviceName: 'tiktok_ad_account', // NOTE: comes back as spotify_artist_page from BE
        },
        [AdConnectionType.FACEBOOK_AD_ACCOUNT]: {
            title: 'Facebook Ad Account',
            subtitle: `Select the Facebook Ad Account you'd like to use to run ads.`,
            serviceName: 'facebook_ad_account',
        },
        [AdConnectionType.FACEBOOK_PAGE]: {
            title: 'Facebook Page',
            subtitle: `Select the Facebook Page you'd like to use to run ads.`,
            serviceName: 'facebook_page',
        },
        [AdConnectionType.FACEBOOK_PIXEL]: {
            title: 'Facebook Pixel',
            subtitle: `Select the Facebook Pixel you'd like to use to run ads.`,
            serviceName: 'facebook_pixel',
        },
        [AdConnectionType.FACEBOOK_CONVERSION_EVENT]: {
            title: 'Facebook Conversion Event',
            subtitle: `Select the Facebook Conversion Event you'd like to use to run ads.`,
            serviceName: 'facebook_conversion_event',
        },
        [AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE]: {
            title: 'Custom Audiences',
            subtitle: `Select the Custom Audiences you'd like to import for targeting.`,
            serviceName: 'facebook_custom_audience',
        },
        [AdConnectionType.FACEBOOK_PROFILE]: {
            title: 'Facebook Profile',
            subtitle: `Connect your Facebook account to manage ads and access Facebook features.`,
            serviceName: 'facebook_profile',
        }
    };

    const adConnectTitle = connectionOptions[connectionType]?.title || 'Ad Account';
    const adConnectModalTitle = `${adConnectTitle}` || 'Connect Ad Account';
    const adConnectSubtitle = subtitle || connectionOptions[connectionType]?.subtitle || `What is the username or URL for ${currentBrand.name}'s ${adConnectTitle} account?`
    const adConnectFooter = connectionOptions[connectionType]?.footer;

    const [searchText, setSearchText] = useState('');
    const [isConnecting, setIsConnecting] = useState(false);
    const [filteredSearchResultsList, setFilteredSearchResultsList] = useState<AdConnectionData[]>([]);
    const [searchResultsList, setSearchResultsList] = useState<AdConnectionData[]>([]);
    const [inputError, setInputError] = useState<string | null>(null);
    const [currentlySelectedSearchResultId, setCurrentlySelectedSearchResultId] = useState<FormattedAdConnectionData | null>(null);
    const [isErrorComponentVisible, setIsErrorComponentVisible] = useState(false);

    // State for custom audiences
    const [selectedCustomAudiences, setSelectedCustomAudiences] = useState<FBCustomAudience[]>([]);
    const [isLoadingCustomAudiences, setIsLoadingCustomAudiences] = useState(false);
    const [hasMoreCustomAudiences, setHasMoreCustomAudiences] = useState(false);
    const [customAudiencesAfterCursor, setCustomAudiencesAfterCursor] = useState<string | null>(null);
    const [isLoadingMoreCustomAudiences, setIsLoadingMoreCustomAudiences] = useState(false);
    const [searchTimeoutId, setSearchTimeoutId] = useState<NodeJS.Timeout | null>(null);

    // State for copy from existing brand flow
    const [showCopyFromBrandView, setShowCopyFromBrandView] = useState(false);
    const [isCopyingFromBrand, setIsCopyingFromBrand] = useState(false);

    useEffect(() => {
        setInputError(null);
        setIsErrorComponentVisible(false);

        if (!searchText) {
            setFilteredSearchResultsList(searchResultsList);
        }
    }, [searchText]);

    useEffect(() => {
        if (!isOpen) {
            setSearchText('');
            // Reset custom audiences selection when modal closes
            setSelectedCustomAudiences([]);
            setCustomAudiencesAfterCursor(null);
            setHasMoreCustomAudiences(false);
            // Clear any pending search timeout
            if (searchTimeoutId) {
                clearTimeout(searchTimeoutId);
                setSearchTimeoutId(null);
            }
        }
    }, [isOpen]);

    const getAdConnectionLoadingState = () => {
        let isLoading = false;
        switch (connectionType) {
            case AdConnectionType.TIKTOK_AD_ACCOUNT:
                isLoading = false;
                break;
            case AdConnectionType.FACEBOOK_AD_ACCOUNT:
                isLoading = isLoadingFacebookAdAccounts;
                break;
            case AdConnectionType.FACEBOOK_PAGE:
                isLoading = isLoadingFacebookPages;
                break;
            case AdConnectionType.FACEBOOK_PIXEL:
                isLoading = isLoadingFacebookPixels;
                break;
            case AdConnectionType.FACEBOOK_CONVERSION_EVENT:
                isLoading = isLoadingFacebookConversionEvents;
                break;
            case AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE:
                isLoading = isLoadingCustomAudiences;
                break;
            default:
                break;
        }
        return isLoading;
    }

    let isLoadingSearchResults = getAdConnectionLoadingState();

    // NOTE: this is purely for display purposes. It's not used for the API call and it's not saved to the database
    const formatAdConnectionData = (data: AdConnectionData): FormattedAdConnectionData | null => {
        let newFormattedData: FormattedAdConnectionData | null = null;
        if (!data) {
            return null;
        }

        switch (connectionType) {
            case AdConnectionType.TIKTOK_AD_ACCOUNT: {
                const tikTokData = data as TikTokAdAccount;
                newFormattedData = {
                    id: tikTokData.advertiser_id,
                    title: tikTokData.name,
                    status: tikTokData.status,
                    additionalInfo: `${formatNumberToDisplayType(tikTokData.balance ?? 0, 'currency')} Balance in Account`,
                }
                break;
            }
            case AdConnectionType.FACEBOOK_AD_ACCOUNT: {
                const fbAdData = data as AdAccountPageType;
                let issueDetails = null;
                
                // Use the comprehensive error message from the hook's validation
                if (fbAdData.errorMessage) {
                    // Determine the appropriate action based on the type of error
                    let actionText = 'Review your account';
                    let actionUrl = 'https://business.facebook.com/accountquality';
                    
                    if (fbAdData.errorMessage.includes('permissions')) {
                        actionText = 'Manage permissions';
                        actionUrl = 'https://business.facebook.com/settings/ad-accounts';
                    } else if (fbAdData.errorMessage.includes('payment')) {
                        actionText = 'Add payment method';
                        actionUrl = 'https://business.facebook.com/billing_hub/payment_methods';
                    }
                    
                    issueDetails = {
                        title: fbAdData.errorMessage,
                        actionText,
                        actionUrl,
                    }
                }
                
                newFormattedData = {
                    id: fbAdData.id,
                    title: fbAdData.name,
                    additionalInfo: `${formatNumberToDisplayType(fbAdData.amount_spent, 'currency')} spent`,
                    parentName: `Business: ${fbAdData.business?.name}`,
                    parentId: fbAdData.business?.id,
                    issueDetails: issueDetails ?? undefined,
                    // status: fbAdData.is_published ? 'Active' : 'Inactive',
                }
                break;
            }
            case AdConnectionType.FACEBOOK_PAGE: {
                const fbPageData = data as FacebookPageType;
                const linkedIg = fbPageData.connected_instagram_account?.username;
                newFormattedData = {
                    id: fbPageData.id,
                    title: fbPageData.name,
                    status: fbPageData.is_published ? 'Active' : 'Inactive',
                    thumbnail: fbPageData.picture?.data?.url,
                    additionalInfo: (
                        <div style={{
                            color: Colors.greyDark,
                        }}>
                            <div style={Sty.text14}>
                                {millify(fbPageData.followers_count)} followers
                            </div>
                            {linkedIg && (
                                <p style={styx(Sty.text14, { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 })}>
                                    Linked to: <Icon icon="instagram" size={12} className="fbPageLinkedIgIcon" />@{linkedIg}
                                </p>
                            )}
                        </div>
                    )
                }
                break;
            }
            case AdConnectionType.FACEBOOK_CONVERSION_EVENT: {
                const fbConversionEventData = data as PixelConversionEvent;
                newFormattedData = {
                    id: fbConversionEventData.name,
                    hideId: true,
                    title: fbConversionEventData.name,
                    parentName: fbConversionEventData.parent_name ? `Pixel: ${fbConversionEventData.parent_name}` : undefined,
                    parentId: fbConversionEventData.parent_id ?? undefined,
                    additionalInfo: fbConversionEventData?.count && fbConversionEventData.count > 0 ? (
                        <div style={{
                            color: Colors.greyDark,
                        }}>
                            Tracked {formatNumberToDisplayType(fbConversionEventData.count, 'number')} times over the last 30 days
                        </div>
                    ) : undefined,
                }
                break;
            }
            case AdConnectionType.FACEBOOK_PIXEL: {
                const pixelData = data as FBPixel;

                /** Format pixel stats if available */
                let additionalInfo = undefined;
                if (pixelData.stats?.data?.[0]?.data?.length) {
                    const totalEvents = pixelData.stats.data[0].data.reduce((sum, event) => sum + event.count, 0);
                    additionalInfo = `${millify(totalEvents)} total events`;

                    if (pixelData.creation_time) {
                        additionalInfo += ` | Created on ${dayjs(pixelData.creation_time).format('MM/DD/YYYY')}`;
                    }
                }

                /** Check if pixel is available for use */
                let issueDetails = undefined;
                if (pixelData.is_unavailable) {
                    issueDetails = {
                        title: 'This pixel is unavailable and cannot be used',
                        actionText: 'Review in Business Manager',
                        actionUrl: 'https://business.facebook.com/events_manager',
                    }
                }

                newFormattedData = {
                    id: pixelData.id,
                    title: pixelData.name,
                    status: pixelData.is_unavailable ? 'Unavailable' : 'Active',
                    parentName: pixelData.owner_business?.name ? `Business: ${pixelData.owner_business.name}` : undefined,
                    parentId: pixelData.owner_business?.id,
                    additionalInfo,
                    issueDetails,
                }
                break;
            }
            case AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE: {
                const fbCustomAudienceData = data as FBCustomAudience;
                let sizeLabel = '';
                
                if (fbCustomAudienceData.approximate_count_lower_bound > 0 || fbCustomAudienceData.approximate_count_upper_bound > 0) {
                    if (fbCustomAudienceData.approximate_count_lower_bound === fbCustomAudienceData.approximate_count_upper_bound) {
                        sizeLabel = `Approx. ${millify(fbCustomAudienceData.approximate_count_lower_bound)} people`;
                    } else {
                        sizeLabel = `Approx. ${millify(fbCustomAudienceData.approximate_count_lower_bound)} - ${millify(fbCustomAudienceData.approximate_count_upper_bound)} people`;
                    }
                }
                
                newFormattedData = {
                    id: fbCustomAudienceData.id,
                    title: fbCustomAudienceData.name,
                    hideId: true,
                    status: fbCustomAudienceData.delivery_status?.description || 'Active',
                    additionalInfo: sizeLabel || 'Size unknown',
                }
                break;
            }
            default:
                return null;
        }

        return newFormattedData;
    }

    const rowAlreadySelected = (id: number | string) => {
        switch (connectionType) {
            case AdConnectionType.TIKTOK_AD_ACCOUNT:
                return Number(tiktokAdAccount?.advertiser_id) === id;
            case AdConnectionType.FACEBOOK_AD_ACCOUNT:
                return selectedFacebookAdAccount?.id === id;
            case AdConnectionType.FACEBOOK_PAGE:
                return Number(selectedFacebookPage?.id) === id;
            case AdConnectionType.FACEBOOK_PIXEL:
                return selectedFacebookPixel?.id === id;
            case AdConnectionType.FACEBOOK_CONVERSION_EVENT:
                return conversionsEventName === id;
            case AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE:
                return selectedCustomAudiences.some(audience => audience.id === id);
            default:
                return false;
        }
    }

    /** Toggle custom audience selection for multiple selection */
    const toggleCustomAudienceSelection = (audience: FBCustomAudience) => {
        const isSelected = selectedCustomAudiences.some(a => a.id === audience.id);

        if (isSelected) {
            setSelectedCustomAudiences(selectedCustomAudiences.filter(a => a.id !== audience.id));
        } else {
            setSelectedCustomAudiences([...selectedCustomAudiences, audience]);
        }
    };

    /** Import selected custom audiences */
    const importSelectedCustomAudiences = () => {
        if (onImportMultiple && selectedCustomAudiences.length > 0) {
            onImportMultiple(selectedCustomAudiences);
            onClose();
        }
    };

    /** Handle copying connection from an existing brand */
    const handleCopyFromBrand = async (sourceBrand: CurrentBrand) => {
        if (!onCopyFromBrand) return;
        
        try {
            setIsCopyingFromBrand(true);
            await onCopyFromBrand(sourceBrand);
            onClose();
        } catch (error) {
            console.error('Error copying from brand:', error);
            toastError('Failed to copy connection. Please try again.');
        } finally {
            setIsCopyingFromBrand(false);
        }
    };

    const handleOnConnect = async ({
        dataFromList,
    }: {
        dataFromList: AdConnectionData,
    }) => {
        try {
            setIsConnecting(true);
            setCurrentlySelectedSearchResultId(formatAdConnectionData(dataFromList));
            let data: AdConnectionData | null = dataFromList;

            let updatedCurrentBrand: CurrentBrand | null = currentBrand;

            switch (connectionType) {
                case AdConnectionType.TIKTOK_AD_ACCOUNT:
                    await setTiktokAdAccount(data as TikTokAdAccount);
                    break;
                case AdConnectionType.FACEBOOK_AD_ACCOUNT:
                    await setFacebookAdAccount(data as AdAccountPageType);
                    break;
                case AdConnectionType.FACEBOOK_PAGE:
                    let fbPage = data as FacebookPageType;
                    await setFacebookPage(fbPage);
                    break;
                case AdConnectionType.FACEBOOK_PIXEL:
                    let fbPixel = data as FBPixel;
                    await setFacebookPixel(fbPixel);
                    break;
                case AdConnectionType.FACEBOOK_CONVERSION_EVENT:
                    let fbConversionEvent = data as PixelConversionEvent;
                    setConversionsEventName(fbConversionEvent.name);
                    break;
                case AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE:
                    if (allowMultipleSelection) {
                        toggleCustomAudienceSelection(data as FBCustomAudience);
                        return; // Don't close modal for multiple selection
                    } else if (onConnect) {
                        onConnect(data);
                    }
                    break;
                default:
                    break;
            }
            
            onClose();
        } catch (error) {
            console.error('Error connecting account:', error);
            toastError('There was an error connecting your account. Please try again.');
        } finally {
            setIsConnecting(false);
        }
    }

    const getadConnectFooter = () => {

        return (
            <div
                className="adConnectFooter"
                style={sty.adConnectFooter}
            >
                {adConnectFooter}
            </div>
        )
    }



    /** Loads more custom audiences (pagination) */
    const loadMoreCustomAudiences = async () => {
        if (!customAudiencesAfterCursor || isLoadingMoreCustomAudiences) return;

        const targetBrandId = brandId || currentBrand?.id;
        if (!targetBrandId) return;

        try {
            setIsLoadingMoreCustomAudiences(true);
            const moreAudiences = await getFacebookCustomAudiences(targetBrandId, undefined, {
                search: searchText || undefined,
                limit: 50,
                after: customAudiencesAfterCursor
            });
            
            if (moreAudiences.length > 0) {
                const updatedList = [...searchResultsList, ...moreAudiences];
                setSearchResultsList(updatedList);
                setFilteredSearchResultsList(updatedList);
            }
        } catch (error) {
            console.error('Error loading more custom audiences:', error);
        } finally {
            setIsLoadingMoreCustomAudiences(false);
        }
    };

    /** Unified debounced search for all connection types */
    const debouncedSearch = async (query: string) => {
        // Clear existing timeout
        if (searchTimeoutId) {
            clearTimeout(searchTimeoutId);
        }

        // Set new timeout
        const timeoutId = setTimeout(async () => {
            try {
                // Update search text state
                setSearchText(query);
                // Trigger data reload with new search term
                await handleGetData(true);
            } catch (error) {
                console.error('Error searching:', error);
            }
        }, 500); // 500ms debounce

        setSearchTimeoutId(timeoutId);
    };

    const handleGetData = async (resetList: boolean = true) => {
        const targetBrandId = brandId || currentBrand?.id;
        if (!targetBrandId) {
            console.error('No brand ID found for loading connection data');
            return;
        }

        switch (connectionType) {
            case AdConnectionType.TIKTOK_AD_ACCOUNT:
                const adAccounts = await getTikTokAdAccounts();
                setSearchResultsList(adAccounts);
                setFilteredSearchResultsList(adAccounts);
                break;
            case AdConnectionType.FACEBOOK_AD_ACCOUNT:
                try {
                    const fbAdAccounts = await getFacebookAdAccounts(undefined, targetBrandId, {
                        search: searchText || undefined,
                        limit: 50
                    });
                    if (resetList) {
                        setSearchResultsList(fbAdAccounts);
                        setFilteredSearchResultsList(fbAdAccounts);
                    }
                } catch (error) {
                    console.error('Error loading Facebook ad accounts:', error);
                }
                break;
            case AdConnectionType.FACEBOOK_PAGE:
                try {
                    const fbPages = await getFacebookPages(targetBrandId, {
                        search: searchText || undefined,
                        limit: 50,
                        includeInstagram: true
                    });
                    if (resetList) {
                        setSearchResultsList(fbPages);
                        setFilteredSearchResultsList(fbPages);
                    }
                } catch (error) {
                    console.error('Error loading Facebook pages:', error);
                }
                break;
            case AdConnectionType.FACEBOOK_PIXEL:
                try {
                    const fbPixels = await getFacebookPixels(targetBrandId, undefined, {
                        search: searchText || undefined,
                        limit: 50
                    });
                    if (resetList) {
                        setSearchResultsList(fbPixels);
                        setFilteredSearchResultsList(fbPixels);
                    }
                } catch (error) {
                    console.error('Error loading Facebook pixels:', error);
                }
                break;
            case AdConnectionType.FACEBOOK_CONVERSION_EVENT:
                try {
                    const fbConversionEvents = await getConversionEvents(targetBrandId, undefined, {
                        search: searchText || undefined,
                        limit: 50
                    });
                    if (resetList) {
                        setSearchResultsList(fbConversionEvents);
                        setFilteredSearchResultsList(fbConversionEvents);
                    }
                } catch (error) {
                    console.error('Error loading Facebook conversion events:', error);
                }
                break;
            case AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE:
                try {
                    setIsLoadingCustomAudiences(true);
                    const customAudiences = await getFacebookCustomAudiences(targetBrandId, undefined, {
                        search: searchText || undefined,
                        limit: 50
                    });
                    if (resetList) {
                        setSearchResultsList(customAudiences);
                        setFilteredSearchResultsList(customAudiences);
                    }
                } catch (error) {
                    console.error('Error loading custom audiences:', error);
                } finally {
                    setIsLoadingCustomAudiences(false);
                }
                break;
            default:
                break;
        }
    }

    const renderadConnectIcon = () => {
        switch (connectionType) {
            case AdConnectionType.TIKTOK_AD_ACCOUNT:
                return (
                    <Icon
                        className="adConnectIconTiktok"
                        icon="tiktok"
                        size={ICON_SIZE}
                        style={sty.adConnectIcon}
                    />
                );
            default:
                return null;
        }
    }


    const renderEmptyLoadingState = () => {
        return (
            <div className="adConnectListEmptyLoadingState" style={sty.adConnectListEmptyLoadingState}>
                <Loader />
            </div>
        )
    }

    const userIsLoggedIntoPlatform = () => {
        switch (connectionType) {
            case AdConnectionType.FACEBOOK_AD_ACCOUNT:
            case AdConnectionType.FACEBOOK_PAGE:
            case AdConnectionType.FACEBOOK_PIXEL:
            case AdConnectionType.FACEBOOK_CONVERSION_EVENT:
            case AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE:
                return loggedInFacebookUser;
            default:
                return false;
        }
    }

    // if from Search Term, we want to show a different zero state
    // if not from search term, that means the user doesnt have any pages / ad accounts, etc connected
    const renderZeroState = (fromSearchTerm: boolean = false) => {
        if (fromSearchTerm) {
            return (
                <ConnectionZeroState
                    className="adConnectZeroState"
                    icon="info"
                    title="No results found"
                    subtitle="Try a different search term"
                />
            )
        }
        const config = ZERO_STATE_CONFIGS[connectionType];
        if (!config) {
            return null;
        }

        return (
            <ConnectionZeroState
                className="adConnectZeroState"
                icon={config.icon}
                title={config.title}
                subtitle={config.subtitle}
                primaryButton={config.primaryButton}
                secondaryButton={config.secondaryButton}
            />
        );
    }

    /** Render the copy from existing brand view */
    const renderCopyFromBrandView = () => {
        const brandsWithConnection = availableBrandsForCopy.filter(brand => {
            const connectionDetails = getBrandConnectionDetails(brand);
            return connectionDetails.connected;
        });

        if (brandsWithConnection.length === 0) {
            return (
                <ConnectionZeroState
                    className="copyFromBrandZeroState"
                    icon="info"
                    title="No Connected Brands Found"
                    subtitle={`None of your other brands have a ${connectionOptions[connectionType]?.title} connected yet.`}
                    primaryButton={{
                        text: 'Connect Manually',
                        variant: 'primary',
                        onClick: () => setShowCopyFromBrandView(false)
                    }}
                />
            );
        }

        return (
            <div className="copyFromBrandList" style={sty.adConnectList}>
                {brandsWithConnection.map((brand) => {
                    const connectionDetails = getBrandConnectionDetails(brand);
                    
                    return (
                        <div
                            key={brand.id}
                            className="copyFromBrandItem"
                            style={sty.adConnectListItemRow}
                        >
                            <div className="copyFromBrandItemContent" style={sty.adConnectListItemRowContent}>
                                <div className="copyFromBrandItemLeft" style={sty.adConnectListItemRowLeft}>
                                    <div className="copyFromBrandItemInfo" style={sty.adConnectListItemRowLeftTopContent}>
                                        {brand.image && (
                                            <Icon
                                                iconType="custom"
                                                className="copyFromBrandThumbnail"
                                                icon={brand.image}
                                                size={40}
                                                style={{ marginRight: 8 }}
                                            />
                                        )}
                                        <div>
                                            <div style={sty.adConnectListItemRowLeftTop}>
                                                {brand.name}
                                            </div>
                                            <div style={sty.adConnectListItemRowInfo}>
                                                <div style={sty.adConnectListItemRowAdditionalInfo}>
                                                    {connectionDetails.name} {connectionDetails.details && `• ${connectionDetails.details}`}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <ButtonSymphony
                                    className="copyFromBrandButton"
                                    style={sty.adConnectListItemButton}
                                    variant="outlined"
                                    onClick={() => handleCopyFromBrand(brand)}
                                    disabled={isCopyingFromBrand}
                                    isLoading={isCopyingFromBrand}
                                >
                                    Copy Connection
                                </ButtonSymphony>
                            </div>
                        </div>
                    );
                })}
                <div className="copyFromBrandManualOption" style={sty.copyFromBrandManualOption}>
                    <ButtonSymphony
                        className="copyFromBrandManualButton"
                        variant="text"
                        onClick={() => setShowCopyFromBrandView(false)}
                    >
                        Or connect manually instead
                    </ButtonSymphony>
                </div>
            </div>
        );
    };

    const renderConnectList = () => {
        // Show copy from brand view if enabled and selected
        if (showCopyFromBrandView) {
            return renderCopyFromBrandView();
        }

        // Handle loading states for both Facebook and other platforms
        if (isLoadingSearchResults) {
            return renderEmptyLoadingState();
        }

        if (!userIsLoggedIntoPlatform()) {
            return renderFacebookLoginButton();
        }

        // Handle empty states for all connection types
        const listToRender = filteredSearchResultsList;
        if (!listToRender?.length) {
            return renderZeroState(searchText.length > 0);
        }

        return (
            <div
                className="adConnectList"
                style={styx(
                    sty.adConnectList,
                    !mobileView && sty.adConnectListDesktop,
                )}
            >
                {listToRender.map((result, index) => {
                    const formattedResult = formatAdConnectionData(result);
                    if (!formattedResult || !formattedResult.id) {
                        return null;
                    }

                    const {
                        id,
                        hideId,
                        title,
                        parentName,
                        parentId,
                        issueDetails,
                        additionalInfo,
                        thumbnail
                    } = formattedResult;

                    let isAlreadyConnectedRow = rowAlreadySelected(id);
                    return (
                        <div
                            key={`${id}-${index}`}
                            className={clsx('adConnectListItemRow')}
                            style={sty.adConnectListItemRow}
                        >
                            <div className="adConnectListItemRowContent" style={sty.adConnectListItemRowContent}>
                                <div className="adConnectListItemRowLeft" style={sty.adConnectListItemRowLeft}>
                                    <div className="adConnectListItemRowLeftTopContent" style={sty.adConnectListItemRowLeftTopContent}>
                                        {thumbnail && (
                                            <Icon
                                                iconType="custom"
                                                className="adConnectListItemRowLeftTopThumbnail"
                                                icon={thumbnail}
                                                size={40}
                                                style={{
                                                    marginRight: 4
                                                }}
                                            />
                                        )}
                                        <div className="adConnectListItemRowLeftTop" style={sty.adConnectListItemRowLeftTop}>
                                            {title}
                                            {!hideId && (
                                                <span style={{ color: Colors.greyDark }}> ({id})</span>
                                            )}
                                            {(parentName || additionalInfo) && (
                                                <div className="adConnectListItemRowInfo" style={sty.adConnectListItemRowInfo}>
                                                    {additionalInfo && (
                                                        <div className="adConnectListItemRowAdditionalInfo" style={sty.adConnectListItemRowAdditionalInfo}>
                                                            {additionalInfo}
                                                        </div>
                                                    )}
                                                    {parentName && parentId && (
                                                        <div className="adConnectListItemRowParentInfo" style={sty.adConnectListItemRowParentInfo}>
                                                            {parentName} ({parentId})
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                </div>
                                {connectionType === AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE && allowMultipleSelection ? (
                                    <ButtonSymphony
                                        className="adConnectListItemButton"
                                        style={sty.adConnectListItemButton}
                                        variant={isAlreadyConnectedRow ? "primary" : "outlined"}
                                        onClick={() => handleOnConnect({ dataFromList: result })}
                                        disabled={isConnecting}
                                        isLoading={isConnecting && currentlySelectedSearchResultId?.id === id}
                                    >
                                        {isAlreadyConnectedRow ? 'Selected' : 'Select'}
                                    </ButtonSymphony>
                                ) : !isAlreadyConnectedRow ? (
                                    <ButtonSymphony
                                        className="adConnectListItemButton"
                                        style={sty.adConnectListItemButton}
                                        variant="outlined"
                                        onClick={() => handleOnConnect({ dataFromList: result })}
                                        disabled={isConnecting || Boolean(issueDetails)}
                                        isLoading={isConnecting && currentlySelectedSearchResultId?.id === id}
                                    >
                                        Connect
                                    </ButtonSymphony>
                                ) : (
                                    <ButtonSymphony

                                        className="adConnectListItemButton"
                                        disabled
                                        variant="text"
                                        style={sty.adConnectListItemButton}
                                    >
                                        Connected
                                    </ButtonSymphony>
                                )}
                            </div>
                            {issueDetails && (
                                <div className="errorContainer" style={sty.errorContainer}>
                                    <p>{issueDetails.title}</p>
                                    {issueDetails.actionText && (
                                        <ButtonSymphony
                                            variant="text"
                                            iconRight="click"
                                            size="small"
                                            className="adConnectListItemRowIssueDetailsButton"
                                            onClick={() => window.open(issueDetails.actionUrl, '_blank')}
                                        >
                                            {issueDetails.actionText}
                                        </ButtonSymphony>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}

                {/* Load More button for custom audiences */}
                {connectionType === AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE && hasMoreCustomAudiences && (
                    <div className="loadMoreContainer" style={sty.loadMoreContainer}>
                        <ButtonSymphony
                            className="loadMoreButton"
                            variant="outlined"
                            onClick={loadMoreCustomAudiences}
                            disabled={isLoadingMoreCustomAudiences}
                            isLoading={isLoadingMoreCustomAudiences}
                        >
                            Load More
                        </ButtonSymphony>
                    </div>
                )}



            </div>
        )
    }

    const handleFilterSearchResults = () => {
        // For Facebook connection types that support backend search, use debounced search
        if ([
            AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE,
            AdConnectionType.FACEBOOK_AD_ACCOUNT,
            AdConnectionType.FACEBOOK_PAGE,
            AdConnectionType.FACEBOOK_PIXEL,
            AdConnectionType.FACEBOOK_CONVERSION_EVENT
        ].includes(connectionType)) {
            if (searchText.trim() === '') {
                // If search is cleared, reload the full list
                handleGetData();
            } else {
                debouncedSearch(searchText);
            }
        } else {
            // Use local filtering for other connection types
            const filteredResults = searchResultsList.filter((result) => {
                const formattedResult = formatAdConnectionData(result);
                if (!formattedResult) {
                    return false;
                }
                return formattedResult.title.toLowerCase().includes(searchText.toLowerCase());
            });
            setFilteredSearchResultsList(filteredResults);
        }
    }

    const renderSearchInput = () => {
        return (
            <InputSymphony
                isSearch
                onSearch={() => handleFilterSearchResults()}
                className="adConnectModalSearchInput"
                value={searchText}
                onChange={(valueText) => setSearchText(valueText)}
                placeholder={`Search for an account`}
            />
        )
    }


    const renderInputError = () => {
        if (inputError) {
            return (
                <div className="adConnectModalInputError"
                    style={sty.adConnectModalInputError}
                >
                    {inputError}
                </div>
            )
        }
        return null;
    }

    const renderErrorComponent = () => {
        if (!isErrorComponentVisible) {
            return null;
        }

        switch (connectionType) {
            default:
                return null;
        }
    }


    const ConnectionZeroState = ({
        className,
        style,
        icon,
        iconSize = 48,
        title,
        subtitle,
        primaryButton,
        secondaryButton,
    }: {
        className?: string;
        style?: CSSProperties;
        icon: string;
        iconSize?: number;
        title: string;
        subtitle: string;
        primaryButton?: ButtonConfig;
        secondaryButton?: ButtonConfig;
    }) => {
        return (
            <div
                className={`connectionZeroState ${className || ''}`}
                style={{
                    ...sty.connectionZeroState,
                    ...style
                }}
            >
                <Icon
                    className="connectionZeroStateIcon"
                    icon={icon}
                    size={iconSize}
                    style={sty.connectionZeroStateIcon}
                />
                <div style={sty.connectionZeroStateContent}>
                    <h3 style={sty.connectionZeroStateTitle}>{title}</h3>
                    <p style={sty.connectionZeroStateSubtitle}>
                        {subtitle}
                    </p>
                </div>
                {(primaryButton || secondaryButton) && (
                    <div
                        className="connectionZeroStateButtonsContainer"
                        style={sty.connectionZeroStateButtons}>
                        {primaryButton && <ButtonSymphony
                            className="connectionZeroStatePrimaryButton"
                            variant={primaryButton.variant || 'primary'}
                            size={primaryButton?.size || 'medium'}
                            onClick={primaryButton?.onClick}
                            iconRight={primaryButton?.iconRight}
                        >
                            {primaryButton.text}
                        </ButtonSymphony>
                        }
                        {secondaryButton && (
                            <ButtonSymphony
                                className="connectionZeroStateSecondaryButton"
                                variant={secondaryButton.variant || 'text'}
                                size={secondaryButton.size || 'medium'}
                                onClick={secondaryButton.onClick}
                                iconRight={secondaryButton.iconRight}
                                style={sty.connectionZeroStateSecondaryButton}
                            >
                                {secondaryButton.text}
                            </ButtonSymphony>
                        )}
                    </div>
                )}
            </div>
        );
    };


    // Determine if we should show the import button for custom audiences
    const shouldShowImportButton = connectionType === AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE && 
                                    allowMultipleSelection && 
                                    selectedCustomAudiences.length > 0;

    // Add selected count to subtitle for custom audiences
    let finalSubtitle = adConnectSubtitle;
    if (connectionType === AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE && allowMultipleSelection && selectedCustomAudiences.length > 0) {
        finalSubtitle += ` (${selectedCustomAudiences.length} selected)`;
    }

    // Update subtitle for copy from brand view
    if (showCopyFromBrandView) {
        finalSubtitle = `Copy the ${connectionOptions[connectionType]?.title} connection from one of your existing brands.`;
    }

    // Update subtitle for prerequisite connections
    if (isPrerequisite && originalConnectionType) {
        const originalTitle = connectionOptions[originalConnectionType]?.title || 'Facebook feature';
        finalSubtitle = `To connect your ${originalTitle}, you first need to connect your Facebook Profile. This gives us permission to access your Facebook features.`;
    }

    // Check if we should show the copy option button
    const shouldShowCopyOption = isTeamView() && showCopyFromExistingBrand && 
                                 availableBrandsForCopy.length > 0 && 
                                 !showCopyFromBrandView &&
                                 availableBrandsForCopy.some(brand => getBrandConnectionDetails(brand).connected);

    return (
        <>
            <ModalSymphony
                className={clsx('adConnectModalComponent', className)}
                style={styx(sty.adConnectModalComponent, style)}
                isOpen={isOpen}
                onClose={onClose}
                title={adConnectModalTitle}
                subtitle={finalSubtitle}
                fullScreen={mobileView}
                {...(shouldShowImportButton && {
                    onClickButtonRight: importSelectedCustomAudiences,
                    buttonRightText: `Import ${selectedCustomAudiences.length} Audience${selectedCustomAudiences.length === 1 ? '' : 's'}`,
                    buttonRightVariant: 'primary' as const,
                })}
                {...(shouldShowCopyOption && {
                    onClickButtonRight: () => setShowCopyFromBrandView(true),
                    buttonRightText: 'Copy from Existing Brand',
                    buttonRightVariant: 'outlined' as const,
                })}
            >
                {renderSearchInput()}
                {renderInputError()}
                {renderErrorComponent()}
                {renderConnectList()}
                {getadConnectFooter()}
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    adConnectModalComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    adConnectInput: {
        width: '100%',
        marginBottom: 2
    },
    adConnectFooter: {
        fontSize: 14,
        color: Colors.greyDark,
    },
    adConnectList: {
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        borderRadius: 8,
        boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.15)'
    },
    adConnectListDesktop: {
        maxHeight: 450,
    },
    adConnectListItemRow: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingLeft: 24,
        paddingRight: 24,
        paddingTop: 16,
        paddingBottom: 16,
        borderBottom: `1px solid ${Colors.greyLight}`,
        gap: 8,
    },
    adConnectListItemRowContent: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
        width: '100%',
    },
    adConnectListItemRowLeft: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        gap: 4,
    },
    adConnectListItemRowLeftTop: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    adConnectIcon: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
    },
    inputIconRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 8,
    },
    adConnectRomImage: {
        height: 40,
        width: 40,
        borderRadius: 8,
        objectFit: 'cover',
        flexShrink: 0,
    },
    adConnectListItemButton: {
        marginLeft: 'auto',
        flexShrink: 0,
        minWidth: 100,
    },
    adConnectModalInputError: {
        color: Colors.red,
        fontSize: 14,
    },
    spotifyNotificationBanner: {
        padding: 16,
        backgroundColor: Colors.yellowLight,
        borderRadius: 16,
    },
    loginButton: {
        width: '100%',
        marginTop: 16,
    },
    adConnectListEmptyLoadingState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 16,
    },
    adConnectListError: {
        padding: 16,
        textAlign: 'center',
    },
    reRequestPermissionsButton: {
        width: '100%',
        marginTop: 16,
        color: Colors.purple,
        fontWeight: 'bold',
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        textAlign: 'center',
    },
    createPageLink: {
        display: 'block',
        width: '100%',
        marginTop: 16,
        color: Colors.purple,
        fontWeight: 'bold',
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        textAlign: 'center',
        textDecoration: 'none',
    },
    facebookPagesHelp: {
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
    },
    facebookPagesHelpText: {
        margin: '16px 0',
        padding: '0 20px',
    },
    linkPageButton: {
        width: '100%',
        color: Colors.purple,
        fontWeight: 600,
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        textAlign: 'center',
    },
    adConnectListItemRowLeftTopContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    pageAccessButton: {
        width: '100%',
        color: Colors.purple,
        fontWeight: 600,
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        textAlign: 'center',
    },
    adConnectListItemRowInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        width: 'fit-content',
    },
    adConnectListItemRowAdditionalInfo: {
        color: Colors.greyDark,
        fontSize: 14,
    },
    adConnectListItemRowParentInfo: {
        backgroundColor: Colors.greyLight,
        padding: '4px 8px',
        borderRadius: 4,
        color: Colors.greyDark,
        fontSize: 12,

        whiteSpace: 'nowrap',
    },
    errorContainer: {
        display: 'flex',
        gap: '12px',
        borderRadius: '8px',
        padding: '8px',
        width: '100%',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: Colors.PAPER_ERROR_CONTAINER,
    },
    adConnectNoFacebookPagesState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 32,
        textAlign: 'center',
    },
    adConnectNoFacebookPagesButton: {
        marginTop: 16,
        color: Colors.purple,
        fontWeight: 'bold',
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
        textAlign: 'center',
    },

    connectionZeroState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 32,
        textAlign: 'center',
    },
    connectionZeroStateIcon: {
        marginBottom: 16,
        opacity: 0.5,
    },
    connectionZeroStateContent: {
        textAlign: 'center',
        marginBottom: 24,
    },
    connectionZeroStateTitle: {
        marginBottom: 8,
        margin: 0,
    },
    connectionZeroStateSubtitle: {
        color: Colors.greyDark,
        margin: 0,
    },
    connectionZeroStateButtons: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
        maxWidth: 300,
        alignItems: 'center'
    },
    connectionZeroStateSecondaryButton: {
        marginTop: 8
    },
    loadMoreContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
    },
    copyFromBrandManualOption: {
        display: 'flex',
        justifyContent: 'center',
        padding: 16,
        borderTop: `1px solid ${Colors.greyLight}`,
    },
}

export default AdConnectionModal;
